'use client';

// Inspired by react-hot-toast library
import * as React from 'react';

import type { ToastVariant } from '@/components/ui/toast';

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000;
/** Default auto-dismiss (ms). Action toasts stay longer so the CTA is usable. */
const TOAST_DURATION_MS = 5000;
const TOAST_ACTION_DURATION_MS = 12000;

/** Structured action — rendered by Toaster with fixed chrome. No className. */
export type ToastActionConfig = {
  label: string;
  altText: string;
  onClick: () => void;
};

/**
 * Public toast API. Variants only — no className, duration, or raw action elements.
 *
 * - `variant`: visual style (`default` | `success` | `destructive`)
 * - `action`: optional CTA button (system styles + longer duration)
 * - `sticky`: stay until dismissed (version-update prompt only)
 * - `actionOnBodyClick`: a click anywhere on the toast (except the X) runs the
 *   action. For toasts whose action is the only reason they exist, so a
 *   near-miss can't dismiss them silently (RUT-3962).
 */
export type ToastInput = {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  action?: ToastActionConfig;
  sticky?: boolean;
  actionOnBodyClick?: boolean;
};

type ToasterToast = ToastInput & {
  id: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
};

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType['ADD_TOAST'];
      toast: ToasterToast;
    }
  | {
      type: ActionType['UPDATE_TOAST'];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType['DISMISS_TOAST'];
      toastId?: ToasterToast['id'];
    }
  | {
      type: ActionType['REMOVE_TOAST'];
      toastId?: ToasterToast['id'];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t,
        ),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

function resolveDuration(input: ToastInput): number {
  if (input.sticky) return Number.POSITIVE_INFINITY;
  if (input.action) return TOAST_ACTION_DURATION_MS;
  return TOAST_DURATION_MS;
}

function toast(input: ToastInput) {
  const id = genId();
  const duration = resolveDuration(input);

  const update = (props: ToastInput) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...props, id, duration: resolveDuration(props) },
    });
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...input,
      id,
      duration,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return {
    id,
    dismiss,
    update,
  };
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  };
}

export { useToast, toast };
export type { ToasterToast };
